import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';
import { showToast } from '../../utils/helpers';


const ViewRejectionDetailsModal = ({ show, onClose, userId, requestedName, onReapply }) => {
  const [rejectionDetails, setRejectionDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isReapplying, setIsReapplying] = useState(false);

  useEffect(() => {
    if (show && userId && requestedName) {
      fetchRejectionDetails();
    } else {
      setRejectionDetails(null);
    }
  }, [show, userId, requestedName]);

  const fetchRejectionDetails = async () => {
    if (!userId || !requestedName) {
      showToast('Missing user information', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.post(API_CONFIG.REFUNDS.REJECTION_DETAILS, {
        userId: userId,
        requestedName: requestedName,
        status: "rejected"
      });
      
      const data = response.data;
      setRejectionDetails(data);
    } catch (error) {
      console.error('Error fetching rejection details:', error);
      
      if (error.response?.status === 404) {
        showToast('No rejection details found for this request', 'warning');
      } else {
        showToast('Failed to load rejection details', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleReapply = async () => {
    setIsReapplying(true);
    try {
      await onReapply();
      onClose();
      showToast('Reapplication submitted successfully!', 'success');
    } catch (error) {
      console.error('Error reapplying:', error);
      showToast('Failed to submit reapplication', 'error');
    } finally {
      setIsReapplying(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold ">Verification Details</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading rejection details...</p>
            </div>
          </div>
        ) : rejectionDetails ? (
          <div className="space-y-6">
            {/* Request Summary */}
            <div className="bg-white rounded-lg p-4">
              <h4 className="font-medium  mb-3">Request Summary</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">From:</span>
                  <p className="font-medium text-gray-900">{rejectionDetails.former_company_name}</p>
                </div>
                <div>
                  <span className="text-gray-600">To:</span>
                  <p className="font-medium text-gray-900">{rejectionDetails.requested_name}</p>
                </div>
                <div>
                  <span className="text-gray-600">Status:</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    rejectionDetails.status === 'rejected' 
                      ? 'bg-red-100 text-red-800'
                      : rejectionDetails.status === 'approved'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {rejectionDetails.status?.toUpperCase()}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Reviewed On:</span>
                  <p className="font-medium text-gray-900">
                    {formatDate(rejectionDetails.rejected_at)}
                  </p>
                </div>
              </div>
            </div>

            {/* Rejection Reason */}
            <div>
              <h4 className="text-lg font-medium  mb-3">
                {rejectionDetails.status === 'rejected' ? 'Reason for Rejection' : 'Review Notes'}
              </h4>
              <div className={`rounded-lg p-4 ${
                rejectionDetails.status === 'rejected' 
                  ? 'bg-red-50 border border-red-200'
                  : 'bg-blue-50 border border-blue-200'
              }`}>
                <p className={`whitespace-pre-wrap ${
                  rejectionDetails.status === 'rejected' ? 'text-red-800' : 'text-blue-800'
                }`}>
                  {rejectionDetails.rejection_reason || 'No specific notes provided.'}
                </p>
              </div>
            </div>

            {/* Reviewer Information */}
            {rejectionDetails.reviewed_by && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-800 mb-2">Reviewed By</h4>
                <p className="text-blue-700">{rejectionDetails.reviewed_by}</p>
              </div>
            )}

            {/* Call to Action for Rejected Status */}
            {rejectionDetails.status === 'rejected' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <svg className="w-5 h-5 text-green-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h5 className="font-medium text-green-800 mb-1">Think this was a mistake?</h5>
                    <p className="text-green-700 text-sm mb-3">
                      You can submit a reapplication with additional information or corrections to address the concerns mentioned above.
                    </p>
                    {/* <button
                      onClick={handleReapply}
                      disabled={isReapplying}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isReapplying ? 'Submitting...' : 'Submit Reapplication'}
                    </button> */}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h4 className="text-lg font-medium text-gray-700 mb-2">No Details Found</h4>
            <p className="text-gray-600 mb-4">We couldn't find any verification details for this request.</p>
            <button
              onClick={fetchRejectionDetails}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewRejectionDetailsModal;